import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import testable from '@xingternal/malt-util/testable';
import { QA_MALT } from '@xingternal/malt-constants';

import styles from './Userbadge.css';

const TestableSpan = testable(QA_MALT, 'span');

export const TYPE_AMBASSADOR = 'AMBASSADOR';
export const TYPE_BASIC = 'BASIC';
export const TYPE_EXECUTIVE = 'EXECUTIVE';
export const TYPE_INSIDER = 'INSIDER';
export const TYPE_MODERATOR = 'MODERATOR';
export const TYPE_MODERATOR_PREMIUM = 'MODERATOR_PREMIUM';
export const TYPE_PREMIUM = 'PREMIUM';
export const TYPE_PROCOACH = 'PROCOACH';
export const TYPE_PROTRAINER = 'PROTRAINER';

const label = {
  [TYPE_AMBASSADOR]: 'Ambassador',
  [TYPE_BASIC]: 'Basic',
  [TYPE_EXECUTIVE]: 'Executive',
  [TYPE_INSIDER]: 'Insider',
  [TYPE_MODERATOR]: 'Moderator',
  [TYPE_MODERATOR_PREMIUM]: 'Moderator',
  [TYPE_PREMIUM]: 'Premium',
  [TYPE_PROCOACH]: 'Coach',
  [TYPE_PROTRAINER]: 'Trainer',
};

export const TYPE_LIST = [
  TYPE_AMBASSADOR,
  TYPE_BASIC,
  TYPE_EXECUTIVE,
  TYPE_INSIDER,
  TYPE_MODERATOR,
  TYPE_MODERATOR_PREMIUM,
  TYPE_PREMIUM,
  TYPE_PROCOACH,
  TYPE_PROTRAINER,
];

class Userbadge extends PureComponent {
  static propTypes = {
    /** You receive one of these via XING One GraphQL. You can import these separately as e.g. `TYPE_AMBASSADOR` or grab the whole array as `TYPE_LIST`. If you get a different response from your backend, e.g. from Rails, please map the response to these given types.
     *
     * In most instances, you can simply `toUpperCase()` the response. Just watch out: There is currently a `group_moderator` response in the Rails response which needs to be mapped to `MODERATOR` (which is a basic member as a moderator).
     */
    type: PropTypes.oneOf(TYPE_LIST),
    /** set this to render the outline version. */
    outline: PropTypes.bool,
    /** add a custom className to the component */
    className: PropTypes.string,
    /** define a testid for the userbadge */
    testId: PropTypes.string,
    /** set this to render a transparent background */
    transparent: PropTypes.bool,
    /** set this to render a small version on all screens */
    small: PropTypes.bool,
    /** set this to render a small or medium version on all screens */
    size: PropTypes.oneOf(['s', 'm']),
    /** set this to render a small version for tablets and up screens **/
    sizeConfined: PropTypes.oneOf(['s', 'm']),
    /** set this to render a small version for desktop and up screens **/
    sizeWide: PropTypes.oneOf(['s', 'm']),
  };

  static defaultProps = {
    type: TYPE_BASIC,
  };

  render() {
    const {
      type,
      outline,
      className,
      testId,
      transparent,
      small,
      size,
      sizeConfined,
      sizeWide,
    } = this.props;
    const hasBasicStyle = [TYPE_BASIC, TYPE_MODERATOR].includes(type);
    const hasPremiumStyle = [
      TYPE_AMBASSADOR,
      TYPE_INSIDER,
      TYPE_PROCOACH,
      TYPE_PROTRAINER,
      TYPE_MODERATOR_PREMIUM,
      TYPE_PREMIUM,
    ].includes(type);
    const hasExecutiveStyle = type === TYPE_EXECUTIVE;
    const isProBadge = type === TYPE_PROCOACH || type === TYPE_PROTRAINER;
    const badgeText = label[type];
    const sizeSmall = size === 's';
    return (
      <TestableSpan
        className={classNames(
          styles.userbadge,
          {
            [styles.basic]: hasBasicStyle && !outline,
            [styles.basicOutline]: hasBasicStyle && outline,
            [styles.premium]: hasPremiumStyle && !outline,
            [styles.premiumOutline]: hasPremiumStyle && outline,
            [styles.executive]: hasExecutiveStyle && !outline,
            [styles.executiveOutline]: hasExecutiveStyle && outline,
            [styles.transparent]: transparent,
            [styles.small]: small || sizeSmall,
            [styles[`confined-${sizeConfined}`]]: sizeConfined,
            [styles[`wide-${sizeWide}`]]: sizeWide,
          },
          className
        )}
        testId={testId}
      >
        <span className={styles.text}>
          {isProBadge && (
            <span
              className={classNames(
                outline ? styles.proLabelOutline : styles.proLabel
              )}
            >
              Pro
            </span>
          )}
          {badgeText}
        </span>
      </TestableSpan>
    );
  }
}

Userbadge.displayName = 'Userbadge';

export default Userbadge;
